import { supabase } from '@/lib/supabase';
import { US_STATES } from '@/data/constants';

/**
 * Single source of truth for project-media limits and lead-locking behaviour.
 *
 * Defaults live here; an admin can override them from Admin → Settings, which
 * writes to the `mkt_settings` table (public read, admin-only write via RLS).
 * Every uploader / card / detail page reads through this module so a limit only
 * ever has to be changed in one place.
 */

export const MEDIA_SETTINGS_KEY = 'listing_media';
export const LEAD_LOCK_SETTINGS_KEY = 'lead_lock';

export interface MediaSettings {
  max_images: number;
  max_documents: number;
  max_image_mb: number;
  max_document_mb: number;
  image_types: string[];
  document_types: string[];
}

export interface LeadLockSettings {
  enabled: boolean;
  hidden_fields: string[];
}

export const DEFAULT_MEDIA: MediaSettings = {
  max_images: 20,
  max_documents: 10,
  max_image_mb: 10,
  max_document_mb: 25,
  image_types: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif'],
  document_types: ['application/pdf'],
};

export const DEFAULT_LEAD_LOCK: LeadLockSettings = {
  enabled: true,
  hidden_fields: ['city', 'zip', 'address', 'contact', 'documents'],
};

/** Fields an admin may choose to hide from subcontractors before purchase. */
export const LOCKABLE_FIELDS = ['city', 'zip', 'address', 'contact', 'documents'] as const;

/** Document kinds a general contractor can attach to a project. */
export const DOCUMENT_KINDS = ['plans', 'blueprints', 'scope', 'permits', 'specs', 'other'] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

/**
 * A project attachment.
 *
 * `path` is the key inside the PRIVATE `listing-documents` bucket — never a
 * public URL. A viewer only ever receives a short-lived signed URL, and only
 * after the `listing-media` edge function has verified their entitlement.
 */
export interface ListingDocument {
  path: string;
  name: string;
  size?: number;
  kind?: DocumentKind;
  type?: string;
}

export const DOCUMENT_BUCKET = 'listing-documents';

/** Parse whatever is stored in mkt_listings.documents into a typed array. */
export const parseDocuments = (raw: unknown): ListingDocument[] => {
  if (!raw) return [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .filter((d) => d && typeof d === 'object' && typeof (d as ListingDocument).path === 'string')
    .map((d) => d as ListingDocument);
};

/** Strip anything that could break layout or be abused when a filename is rendered. */
export const safeFileName = (name: string) =>
  (name || 'document.pdf').replace(/[^A-Za-z0-9 ._\-()&]/g, '').slice(0, 120) || 'document.pdf';


let cache: { media: MediaSettings; lock: LeadLockSettings } | null = null;

export const loadListingSettings = async (force = false) => {
  if (cache && !force) return cache;
  const fallback = { media: DEFAULT_MEDIA, lock: DEFAULT_LEAD_LOCK };
  try {
    const { data } = await supabase.from('mkt_settings').select('key,value').in('key', [MEDIA_SETTINGS_KEY, LEAD_LOCK_SETTINGS_KEY]);
    const rows = (data || []) as { key: string; value: Record<string, unknown> }[];
    const media = rows.find((r) => r.key === MEDIA_SETTINGS_KEY)?.value;
    const lock = rows.find((r) => r.key === LEAD_LOCK_SETTINGS_KEY)?.value;
    cache = {
      media: { ...DEFAULT_MEDIA, ...(media || {}) } as MediaSettings,
      lock: { ...DEFAULT_LEAD_LOCK, ...(lock || {}) } as LeadLockSettings,
    };
    return cache;
  } catch (_e) {
    return fallback;
  }
};

export const saveListingSettings = async (media: MediaSettings, lock: LeadLockSettings) => {
  const rows = [
    { key: MEDIA_SETTINGS_KEY, value: media, updated_at: new Date().toISOString() },
    { key: LEAD_LOCK_SETTINGS_KEY, value: lock, updated_at: new Date().toISOString() },
  ];
  const { error } = await supabase.from('mkt_settings').upsert(rows, { onConflict: 'key' });
  cache = null;
  return error;
};

/** Full US state names so a locked card can show "California" instead of "CA". */
export const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming', DC: 'Washington, D.C.',
};

export const stateName = (code?: string | null) => {
  if (!code) return '';
  const key = code.trim().toUpperCase();
  if (STATE_NAMES[key]) return STATE_NAMES[key];
  return US_STATES.includes(key) ? key : code;
};

/**
 * Location label shown on a card / detail page.
 * Locked  → "California"  (state only, city withheld)
 * Unlocked → "Riverside, CA"
 */
export const locationLabel = (opts: { city?: string | null; state?: string | null; unlocked: boolean }) => {
  const { city, state, unlocked } = opts;
  if (unlocked && city) return `${city}, ${state || ''}`.replace(/,\s*$/, '');
  return stateName(state) || state || '';
};
